import { type Config } from '@common/clients/api/client';
import { type ContextData } from '@common/defaults/ContextData';
import { type PartialUserContext } from '@common/defaults/UserContext';

import { type BaseClientOptions, createBaseClient } from './BaseClient';
import { type ApiFetchOptions } from './types';

const getCookieHeader = (userContext: PartialUserContext | undefined) => {
    const cookies = {
        sAccessToken: userContext?.supertokens?.sAccessToken,
        sRefreshToken: userContext?.supertokens?.sRefreshToken,
        sFrontToken: userContext?.supertokens?.sFrontToken,
        sAntiCsrf: userContext?.supertokens?.sAntiCsrf,
        'st-last-access-token-update': userContext?.supertokens?.['st-last-access-token-update'],
    } as const;

    return {
        Cookie: Object.entries(cookies)
            .filter(([_key, value]) => value)
            .map((entry) => entry.join('='))
            .join('; '),
    };
};

const DEFAULT_TIMEOUT = 10000;

export interface CreateApiClientConfig {
    contextData: Pick<ContextData, 'config' | 'env'>;
    userContext: PartialUserContext | undefined;
    isClientSide: boolean;
    fetchOptions?: Config & ApiFetchOptions;
}

export const createApiClient = ({
    contextData,
    userContext,
    isClientSide,
    fetchOptions,
}: CreateApiClientConfig) => {
    const { config } = contextData;
    const appEnv = contextData.env?.APP_ENV || process.env.APP_ENV || process.env.NODE_ENV;
    const isProdEnv = appEnv === 'production';
    const isDevEnv = appEnv === 'development';

    if (!config?.api) {
        throw new Error('contextData.config.api not yet set');
    }

    const cookieHeader = !isClientSide ? getCookieHeader(userContext) : undefined;
    const internalUrl = !isClientSide && contextData.config.api.internalHost;
    const baseUrl = internalUrl || contextData.config.api.host;

    const clientConfig = {
        useNextClient: true,
        useNewRelic: true,
        useStatusCheck: true,
    } satisfies BaseClientOptions;

    const credentialConfig = (
        !isClientSide || isDevEnv
            ? {
                  credentials: 'include',
              }
            : {
                  credentials: 'same-origin',
              }
    ) satisfies RequestInit;

    const cacheConfig = (
        !isClientSide
            ? {
                  /**
                   *  https://nextjs.org/docs/app/api-reference/functions/fetch
                   *
                   *  `force-cache`: Next.js looks for a matching request in its Data Cache.
                   *  If there is a match and it is fresh, it will be returned from the cache.
                   *  If there is no match or a stale match, Next.js will fetch the resource from the remote server and update the cache with the downloaded resource.
                   */
                  cache: 'force-cache',
                  next: {
                      revalidate: 1,
                  },
              }
            : {
                  cache: 'default',
              }
    ) satisfies RequestInit;

    const defaultHeaders = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...cookieHeader,
    } satisfies HeadersInit;

    const client = createBaseClient(clientConfig, {
        isClientSide,
        userContext,
        fetchOptions: {
            ...credentialConfig,
            ...cacheConfig,
            ...fetchOptions,
            next: {
                ...(cacheConfig?.next || {}),
                ...(fetchOptions?.next || {}),
                tags: isProdEnv ? [] : ['next-web', ...(fetchOptions?.next?.tags || [])],
            },
            baseUrl,
            timeout: fetchOptions?.timeout || DEFAULT_TIMEOUT,
            headers: {
                ...defaultHeaders,
                ...fetchOptions?.headers,
            },
        },
    });

    return client;
};
